// Constants
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { GridColumnToTaskPropertyMap, SortableColumns } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { CustomFieldValueType } from "../service/planner/enumerations/IPlannerEnumerations";
// Utilities
import { compareByDisplayName, compareOrderHints, compareStringForDisplay } from "@ms/uno-utilities/lib/local/Comparators";
import { Temporal } from "temporal-polyfill-lite";
import { getCustomFieldDefinitionId, isCustomFieldColumn } from "./CustomFieldDefinitionUtilities";
// Lodash imports
import { isEqual } from "@ms/uno-utilities/lib/local/ObjectUtilities";
/**
 * Sort order enumeration
 */ export var SortOrder = /*#__PURE__*/ function(SortOrder) {
    SortOrder[SortOrder["Ascending"] = 0] = "Ascending";
    SortOrder[SortOrder["Descending"] = 1] = "Descending";
    return SortOrder;
}({});
const EMPTY_TASK_EXTERNAL_FIELD_METHODS = {
    getFirstAssigneeDisplayName: ()=>null,
    getBucketName: ()=>null,
    getPlanName: ()=>null,
    getCustomFieldValueType: ()=>undefined,
    getCustomFieldChoiceOrder: ()=>undefined
};
function getSortResultForField(taskA, taskB, descriptor) {
    // Keep field-to-comparator wiring centralized so descriptor handling stays consistent.
    switch(descriptor.fieldName){
        case "displayName":
            return compareByDisplayName(taskA, taskB, (task)=>task.displayName);
        case "priority":
            return compareByPriority(taskA, taskB);
        case "dueDateTime":
            return compareByDueDate(taskA, taskB);
        case "finishDateTime":
            return compareByFinishDate(taskA, taskB);
        case "startDateTime":
            return compareByStartDate(taskA, taskB);
        case "status":
            return compareStringForDisplay(taskA.status, taskB.status);
        case "getPersonalViewOrderHint":
            return compareByPersonalViewOrderHint(taskA, taskB);
        case "getPlanViewOrderHint":
            return compareByPlanViewOrderHint(taskA, taskB);
        case "bucketId":
            return compareByBucketName(descriptor.taskExternalFieldMethods.getBucketName)(taskA, taskB);
        case "planId":
            return compareByPlanName(descriptor.taskExternalFieldMethods.getPlanName)(taskA, taskB);
        case "userAssignments":
            return compareByFirstAssigneeDisplayName(descriptor.taskExternalFieldMethods.getFirstAssigneeDisplayName)(taskA, taskB);
        case "goalIds":
            {
                const getGoal = descriptor.taskExternalFieldMethods.getFirstGoalName;
                return getGoal ? compareStringForDisplay(getGoal(taskA), getGoal(taskB)) : 0;
            }
        case "percentComplete":
            return compareByPercentComplete(taskA, taskB);
        default:
            // Unknown field names fall back to no sorting.
            return 0;
    }
}
/**
 * Generates a task sorting comparator based on sort descriptors
 * @param sortDescriptors Array of sort field descriptors
 * @returns Comparison function for tasks
 */ export function generateTasksSortComparator(sortDescriptors, defaultSort) {
    return (a, b)=>{
        let result = 0;
        for (const descriptor of sortDescriptors){
            result = getSortResultForField(a, b, descriptor);
            // Keep evaluating descriptors when there is still a tie.
            if (result === 0) {
                continue;
            }
            if (descriptor.sortOrder === 1) {
                result = -result;
            }
            return result;
        }
        // Use the provided fallback to guarantee deterministic ordering when primary values tie.
        if (defaultSort) {
            result = defaultSort(a, b);
        }
        if (sortDescriptors[0]?.sortOrder === 1) {
            result = -result;
        }
        return result;
    };
}
export function getDefaultTasksSortComparator_ATMView() {
    return generateTasksSortComparator([
        {
            fieldName: "getPersonalViewOrderHint",
            sortOrder: 0,
            taskExternalFieldMethods: EMPTY_TASK_EXTERNAL_FIELD_METHODS
        }
    ]);
}
export function getDefaultTasksSortComparator_PlanView() {
    return generateTasksSortComparator([
        {
            fieldName: "getPlanViewOrderHint",
            sortOrder: 1,
            taskExternalFieldMethods: EMPTY_TASK_EXTERNAL_FIELD_METHODS
        }
    ]);
}
/**
 * Compares tasks by personal-view order hint
 * @param a First task
 * @param b Second task
 * @returns Comparison result: a number between 1 and -1
 */ export function compareByPersonalViewOrderHint(a, b) {
    return compareOrderHints(a.getPersonalViewOrderHint(TaskGroupingOption.None), b.getPersonalViewOrderHint(TaskGroupingOption.None));
}
/**
 * Compares tasks by plan view order hint
 * @param a First task
 * @param b Second task
 * @returns Comparison result: a number between 1 and -1
 */ export function compareByPlanViewOrderHint(a, b) {
    return compareOrderHints(a.getPlanViewOrderHint(TaskGroupingOption.None), b.getPlanViewOrderHint(TaskGroupingOption.None));
}
/**
 * Comparator get method for alphabetical sort on first assignee display name
 */ export function compareByFirstAssigneeDisplayName(getFirstAssigneeDisplayName) {
    return (a, b)=>compareStringForDisplay(getFirstAssigneeDisplayName(a), getFirstAssigneeDisplayName(b));
}
/**
 * Comparator get method for alphabetical sort on bucket name
 */ export function compareByBucketName(getBucketName) {
    return (a, b)=>compareStringForDisplay(getBucketName(a), getBucketName(b));
}
/**
 * Comparator get method for alphabetical sort on plan name
 */ export function compareByPlanName(getPlanName) {
    return (a, b)=>compareStringForDisplay(getPlanName(a), getPlanName(b));
}
/**
 * Compares tasks for custom sort order
 * @param a First task
 * @param b Second task
 * @param customSortOrder Array of task IDs in desired order
 * @returns Comparison result
 */ export function compareForCustomSort(a, b, customSortOrder) {
    const indexA = customSortOrder.indexOf(a.id);
    const indexB = customSortOrder.indexOf(b.id);
    if (indexA === -1 && indexB === -1) {
        return compareByDisplayName(a, b, (task)=>task.displayName);
    }
    if (indexA === -1) {
        return 1;
    }
    if (indexB === -1) {
        return -1;
    }
    return indexA - indexB;
}
/**
 * Gets the finish date for a task
 * @param task Task to get finish date from
 * @returns Task finish date or null
 */ function getTaskFinishDate(task) {
    if (task?.isComplete() && task.finishDateTime) {
        return task.finishDateTime.temporal;
    }
    return task.finishDateTime?.temporal || null;
}
/**
 * Compares tasks by due date
 * Tasks without a due date appear at the bottom.
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByDueDate(a, b) {
    // Compare actual dates using the existing temporal date comparator
    return compareByTemporalDate(a, b, (task)=>task.dueDateTime?.temporal ?? null);
}
/**
 * Compares two tasks by their finish date
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByFinishDate(a, b) {
    // Compare actual dates using the existing temporal date comparator
    return compareByTemporalDate(a, b, (task)=>getTaskFinishDate(task));
}
/**
 * Compares tasks by start date
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByStartDate(a, b) {
    const dateValueAccessor = (task)=>task?.startDateTime?.temporal ?? null;
    return compareByTemporalDate(a, b, dateValueAccessor);
}
/**
 * Compares tasks by priority
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByPriority(a, b) {
    // Lower priority number = higher priority (0 is highest)
    return b.priority - a.priority;
}
/**
 * Compares tasks by percent complete. Ascending: 0% first, 100% last.
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByPercentComplete(a, b) {
    return a.percentComplete - b.percentComplete;
}
/**
 * Compares tasks by their creation instant. Ascending: earlier-created tasks first.
 * `creationInfo.createdDateTime` is always present, so no null handling is needed.
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByCreatedDate(a, b) {
    return Temporal.Instant.compare(a.creationInfo.createdDateTime, b.creationInfo.createdDateTime);
}
/**
 * Compares tasks by progress state (completion status)
 * @param a First task
 * @param b Second task
 * @returns Comparison result
 */ export function compareByProgress(a, b) {
    const progressA = a.isComplete() ? 1 : 0;
    const progressB = b.isComplete() ? 1 : 0;
    const result = progressA - progressB;
    return result !== 0 ? result : compareByDisplayName(a, b, (task)=>task.displayName);
}
/**
 * Compares tasks by display name
 * @param a First task
 * @param b Second task
 */ export function compareByName(a, b) {
    return compareByDisplayName(a, b, (task)=>task.displayName);
}
/**
 * Generic temporal date comparison function
 * @param a First item
 * @param b Second item
 * @param dateValueAccessor Function to extract date from item
 * @returns Comparison result
 */ function compareByTemporalDate(a, b, dateValueAccessor) {
    const dateA = dateValueAccessor(a);
    const dateB = dateValueAccessor(b);
    // Sort null dates last to keep dated tasks grouped first.
    if (!dateA && !dateB) {
        return 0;
    }
    if (!dateA) {
        return 1;
    }
    if (!dateB) {
        return -1;
    }
    if (dateA instanceof Temporal.ZonedDateTime && dateB instanceof Temporal.ZonedDateTime) {
        return Temporal.ZonedDateTime.compare(dateA, dateB);
    }
    if (dateA instanceof Temporal.PlainDate && dateB instanceof Temporal.PlainDate) {
        return Temporal.PlainDate.compare(dateA, dateB);
    }
    // Mixed Temporal types are normalized to Instants to preserve absolute ordering.
    const instantA = dateA instanceof Temporal.ZonedDateTime ? dateA.toInstant() : dateA.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
    const instantB = dateB instanceof Temporal.ZonedDateTime ? dateB.toInstant() : dateB.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
    return Temporal.Instant.compare(instantA, instantB);
}
/**
 * Type-switch comparator for non-null custom field values.
 * Callers are responsible for null handling before invoking this function.
 * Values are the string-encoded form stored on ICustomFieldValue.value.
 * @param choiceOrderIndex Maps a SingleChoiceText choice ID to its display-order index.
 * Required to compare choice values meaningfully; omit for non-choice value types.
 */ function compareNonNullCustomFieldValues(valueA, valueB, valueType, choiceOrderIndex) {
    switch(valueType){
        case CustomFieldValueType.Text:
            return compareStringForDisplay(valueA, valueB);
        case CustomFieldValueType.Number:
            {
                const numA = parseFloat(valueA);
                const numB = parseFloat(valueB);
                const aInvalid = isNaN(numA) || !isFinite(numA);
                const bInvalid = isNaN(numB) || !isFinite(numB);
                if (aInvalid && bInvalid) return 0;
                if (aInvalid) return 1;
                if (bInvalid) return -1;
                return numA - numB;
            }
        case CustomFieldValueType.Date:
            return compareByTemporalDate(valueA, valueB, (val)=>{
                try {
                    return Temporal.PlainDate.from(val);
                } catch  {
                    return null;
                }
            });
        case CustomFieldValueType.Flag:
            return compareFlagValues(valueA, valueB);
        case CustomFieldValueType.SingleChoiceText:
            return compareChoiceValues(valueA, valueB, choiceOrderIndex);
        default:
            return 0;
    }
}
/**
 * Comparator for Flag (Yes/No) custom field values. Values are the strings "true"/"false".
 * Ascending order places No (false) before Yes (true). Unrecognized values sort last.
 */ function compareFlagValues(valueA, valueB) {
    const rank = (value)=>value === "false" ? 0 : value === "true" ? 1 : 2;
    return rank(valueA) - rank(valueB);
}
/**
 * Comparator for SingleChoiceText custom field values. Values are choice IDs, ordered by the
 * field's display order via choiceOrderIndex. Choices missing from the map (e.g. deleted options)
 * sort after known choices. Without an index map, choice values are treated as equal.
 */ function compareChoiceValues(valueA, valueB, choiceOrderIndex) {
    if (!choiceOrderIndex) return 0;
    const indexA = choiceOrderIndex.get(valueA) ?? Number.MAX_SAFE_INTEGER;
    const indexB = choiceOrderIndex.get(valueB) ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
}
/**
 * Builds a choice-ID → display-order-index lookup from an ordered list of choice IDs.
 * Returns undefined when no order is available so callers can skip choice-specific handling.
 */ function buildChoiceOrderIndex(orderedChoiceIds) {
    if (!orderedChoiceIds) return undefined;
    const index = new Map();
    orderedChoiceIds.forEach((choiceId, position)=>index.set(choiceId, position));
    return index;
}
/**
 * Compares two tasks by the value of a custom field.
 * Missing values are ordered by field type: Date fields sort them to the end, non-Date fields
 * sort them to the beginning. For SingleChoiceText, deleted/unknown choice IDs are treated as missing.
 * Dispatches to type-specific comparison based on the field's value type.
 *
 * @param taskA First task
 * @param taskB Second task
 * @param fieldId The custom field definition ID
 * @param valueType The custom field value type (Text, Number, Date, Flag, or SingleChoiceText)
 * @param choiceOrderIndex Maps a SingleChoiceText choice ID to its display-order index; required
 * to order choice values, ignored for other value types
 * @returns Comparison result: negative if a < b, 0 if equal, positive if a > b
 */ export function compareByCustomField(taskA, taskB, fieldId, valueType, choiceOrderIndex) {
    let valueA = taskA.customFieldValues.find((v)=>v.customFieldDefinitionId === fieldId)?.value;
    let valueB = taskB.customFieldValues.find((v)=>v.customFieldDefinitionId === fieldId)?.value;
    // For SingleChoiceText, treat deleted/unknown choice IDs as missing values so null ordering
    // matches getTaskSortAccessor's behavior.
    if (valueType === CustomFieldValueType.SingleChoiceText && choiceOrderIndex) {
        if (valueA != null && !choiceOrderIndex.has(valueA)) valueA = undefined;
        if (valueB != null && !choiceOrderIndex.has(valueB)) valueB = undefined;
    }
    // Null handling: Date fields sort nulls to the end (consistent with built-in date columns),
    // non-Date fields sort nulls to the beginning.
    if (valueA == null && valueB == null) return 0;
    const nullSentinel = valueType === CustomFieldValueType.Date ? 1 : -1;
    if (valueA == null) return nullSentinel;
    if (valueB == null) return -nullSentinel;
    return compareNonNullCustomFieldValues(valueA, valueB, valueType, choiceOrderIndex);
}
/**
 * Compares two multi-field sort descriptors for equality
 * @param a First sort descriptor
 * @param b Second sort descriptor
 * @returns True if equal, false otherwise
 */ export function areMultiFieldSortDescriptorsEqual(a, b) {
    return isEqual(a, b);
}
function getMappedSortField(columnId) {
    return GridColumnToTaskPropertyMap[columnId];
}
export const getDefaultSortForMyTasksView = (partitionKey)=>{
    switch(partitionKey){
        case MyTasksViewPivotType.All:
            return getDefaultTasksSortComparator_PlannerAllUserGridView();
        case MyTasksViewPivotType.PrivateTasks:
            return getDefaultTasksSortComparator_PlanView();
        default:
            return getDefaultTasksSortComparator_ATMView();
    }
};
const getDefaultTasksSortComparator_PlannerAllUserGridView = ()=>{
    return (a, b)=>{
        // Incomplete tasks before completed tasks
        const completeA = a.isComplete() ? 1 : 0;
        const completeB = b.isComplete() ? 1 : 0;
        if (completeA !== completeB) {
            return completeA - completeB;
        }
        // Sort by due date: overdue → upcoming → no due date
        const dueDateCompare = compareByDueDate(a, b);
        if (dueDateCompare !== 0) {
            return dueDateCompare;
        }
        // Fall back to order hints for stable ordering among equal-date tasks
        const orderHintCompare = compareByPersonalViewOrderHint(a, b);
        if (orderHintCompare !== 0) {
            return orderHintCompare;
        }
        // Deterministic tiebreaker for tasks with identical status, date, and hints
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    };
};
/**
 * Gets the task sort accessor function based on view and sort state
 * @param viewId The current view ID
 * @param sortState The current sort state
 * @param taskExternalFieldMethods The external field methods (to get bucket/plan/assignee names from IDs)
 * @returns A function that compares two tasks based on the current sort state
 */ export const getTaskSortAccessor = (viewId, sortState, taskExternalFieldMethods)=>{
    let defaultSort;
    switch(viewId.name){
        case ViewName.PlanPage:
            defaultSort = getDefaultTasksSortComparator_PlanView();
            break;
        case ViewName.MyTasksPage:
            defaultSort = getDefaultSortForMyTasksView(viewId.partitionKey);
            break;
        default:
            defaultSort = (a, b)=>0;
            break;
    }
    if (!sortState) {
        return defaultSort;
    }
    const { columnId, isAscending } = sortState;
    // Sort state stores a string column id; narrow it for grid-specific checks.
    const typedColumnId = columnId;
    if (SortableColumns.includes(typedColumnId)) {
        const fieldName = getMappedSortField(typedColumnId);
        const sortOrder = isAscending ? 0 : 1;
        if (fieldName) {
            return generateTasksSortComparator([
                {
                    fieldName,
                    sortOrder,
                    taskExternalFieldMethods
                }
            ], defaultSort);
        }
    }
    // Custom field columns use a "cf_" prefix — resolve the definition ID
    // and build a type-aware comparator when the value type is available.
    if (isCustomFieldColumn(columnId)) {
        const fieldId = getCustomFieldDefinitionId(columnId);
        const valueType = taskExternalFieldMethods.getCustomFieldValueType(fieldId);
        if (valueType != null) {
            const direction = isAscending ? 1 : -1;
            // Choice columns sort by the field's defined option order. Precompute the
            // choiceId → index lookup once so the comparator stays O(1) per comparison.
            const choiceOrderIndex = valueType === CustomFieldValueType.SingleChoiceText ? buildChoiceOrderIndex(taskExternalFieldMethods.getCustomFieldChoiceOrder(fieldId)) : undefined;
            // A choice value whose ID is no longer in the field's options renders as an empty
            // cell, so treat it as missing — route it through the same null handling as a
            // genuinely empty value.
            const resolveSortableValue = (raw)=>raw != null && choiceOrderIndex != null && !choiceOrderIndex.has(raw) ? undefined : raw;
            return (a, b)=>{
                const valueA = resolveSortableValue(a.customFieldValues.find((v)=>v.customFieldDefinitionId === fieldId)?.value);
                const valueB = resolveSortableValue(b.customFieldValues.find((v)=>v.customFieldDefinitionId === fieldId)?.value);
                // Null handling: Date fields sort nulls to the end (consistent with built-in
                // date columns), non-Date fields sort nulls to the beginning. The sentinel is
                // multiplied by direction so the position flips when sort order reverses.
                const nullSentinel = valueType === CustomFieldValueType.Date ? 1 : -1;
                if (valueA == null && valueB == null) return defaultSort(a, b) * direction;
                if (valueA == null) return nullSentinel * direction;
                if (valueB == null) return -nullSentinel * direction;
                const cmp = compareNonNullCustomFieldValues(valueA, valueB, valueType, choiceOrderIndex);
                return cmp !== 0 ? cmp * direction : defaultSort(a, b) * direction;
            };
        }
    }
    return defaultSort;
};
